import React, { useState, useRef, useCallback, useMemo } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { Timeline } from "@/components/ui/timeline";
import { AuroraText } from "@/components/ui/aurora-text"; // Assuming this component is defined and imported correctly
import { WavyBackground } from "@/components/ui/wavy-background"; // Assuming this component is defined and imported correctly

// Reusable HighlightedTextBlock component
const HighlightedTextBlock = ({ color, children }) => {
  // Default to blue if no color is provided
  const accentColor = color || "blue";
  
  return (
    <div className="relative">
      {/* Dynamic background color based on accentColor */}
      {/* Tailwind JIT/AOT might require full class names (e.g., 'from-blue-500/20') to be present in source code */}
      {/* If these dynamic classes don't work, ensure your Tailwind CSS configuration is set up for dynamic class generation or use a predefined map. */}
      <div className={`absolute -left-4 w-1 h-full bg-gradient-to-b from-${accentColor}-500/20 to-${accentColor}-600/20 rounded-full`} />
      <div className="relative rounded-lg p-6 bg-white dark:bg-gray-800">
        {children}
      </div>
    </div>
  );
};

// Reusing the generic NotebookIcon from your DS component
const NotebookIcon = React.memo(({ className, color = "currentColor" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M2 6s.5-1 2-1h16c1.5 0 2 1 2 1v14s-.5 1-2 1H4c-1.5 0-2-1-2-1Z"></path>
    <path d="M4 6V4c0-1 .9-2 2-2h10c.6 0 1 .4 1 1"></path>
  </svg>
));
NotebookIcon.displayName = "NotebookIcon";

// Constants for icon colors (reused from DS)
const ACTIVE_ICON_COLOR = "gray"; // CSS keyword 'gray' - ensures it works across themes
const INACTIVE_ICON_COLOR = "rgb(107, 114, 128)"; // Tailwind's gray-500 equivalent

// Define the single navigation item outside the component
const PROMETHEUS_OVERVIEW_ID = "prometheus-overview";
const prometheusNavItem = {
  id: PROMETHEUS_OVERVIEW_ID,
  label: "Digital Twin",
};

// Simple TimelineItem component - it just renders its content prop
const TimelineItem = ({ content, isEven }) => {
  return (
    <div
      // This className was fixed to remove the invalid commented-out line.
      // If you intended animation, re-add logic carefully.
      className={`transform transition-all duration-700 opacity-100`}
    >
      {content} {/* Directly render the content, which already includes HighlightedTextBlock */}
    </div>
  );
};

export function Prometheus() {
  // State to track the active section (will always be the overview in this simplified setup)
  const [activeSectionId, setActiveSectionId] = useState(PROMETHEUS_OVERVIEW_ID);
  const mainContentRef = useRef(null); // Ref for the main scrollable content area

  // Helper to create a slug from a title for use as an ID
  const slugify = (text) => {
    return text
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Remove diacritics
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-") // Replace spaces with -
      .replace(/[^\w-]+/g, "") // Remove all non-word chars
      .replace(/--+/g, "-"); // Replace multiple - with single -
  };

  // The data array for the Timeline component.
  // Using useMemo to ensure it's only created once, as it's static.
  const timelineData = useMemo(() => [
    {
      title: "Purpose",
      id: slugify("Purpose"),
      content: (
        // Changed: HighlightedTextBlock now wraps the entire item's content div
        <HighlightedTextBlock color="blue">
          <div className="space-y-6"> {/* This div is now inside HighlightedTextBlock */}
            <p className="text-neutral-700 dark:text-neutral-300 text-base font-normal leading-relaxed">
              So, Project Prometheus. What's it all about? Basically, I'm trying to build a digital version of myself – like, how close can we actually get to making a computer feel like you're talking to{" "}
              <span className="font-medium text-blue-600 dark:text-blue-400">*me*</span>? It's a bit of an experiment to see if we can create a real reflection of who I am, digitally.
            </p>
            <div className="mt-4 relative">
              <audio controls className="w-full rounded-xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm relative z-10">
                <source src="https://storage.googleapis.com/ashes_project_website_artifacts/audio/TrainingInput.wav" type="audio/wav" />
                Your browser does not support the audio element.
              </audio>
            </div>
          </div>
        </HighlightedTextBlock>
      ),
    },
    {
      title: "Model and Technology Design",
      id: slugify("Model and Technology Design"),
      content: (
        // Changed: HighlightedTextBlock now wraps the entire item's content div
        <HighlightedTextBlock color="green">
          <div className="space-y-6">
            <p className="text-neutral-700 dark:text-neutral-300 text-base font-normal leading-relaxed">
              Okay, so how do we make this digital me happen? The brain of the operation is <span className="font-medium text-emerald-600 dark:text-emerald-400">Hermes</span>, a language model that's already pretty good at text conversations. But to really make it <span className="font-medium text-emerald-600 dark:text-emerald-400">*me*</span>, it needs a voice! That's where things get interesting. I'm checking out two cool tools: Tortoise TTS and Coqui TTS. The idea is to give Hermes a voice that sounds just like mine.
            </p>
            <div className="relative overflow-hidden rounded-xl aspect-video bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/50 dark:to-teal-950/50 p-1">
              <LazyLoadImage
                src="https://6187708.fs1.hubspotusercontent-na1.net/hubfs/6187708/Depositphotos_336298336_XL.jpeg"
                alt="Illustration of technology design"
                className="object-cover w-full h-full rounded-lg"
                effect="opacity"
              />
            </div>
          </div>
        </HighlightedTextBlock>
      ),
    },
    {
      title: "Training Data",
      id: slugify("Training Data"),
      content: (
        // Changed: HighlightedTextBlock now wraps the entire item's content div
        <HighlightedTextBlock color="amber">
          <div className="space-y-6">
            <p className="text-neutral-700 dark:text-neutral-300 text-base font-normal leading-relaxed">
              To teach these TTS models to sound like <span className="font-medium text-amber-600 dark:text-amber-400">*me*</span>, they need to hear me talk! So, I need to feed them samples of my voice. Not just random words, but a good mix of sounds and even some different emotions. This way, the system can really pick up on how I naturally speak and express myself. To get started, I recorded a sample that covers all the letters of the alphabet and my name – it's like a benchmark to see how well these models are learning. Check it out below!
            </p>
            <div className="relative overflow-hidden rounded-xl aspect-video bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/50 dark:to-orange-950/50 p-1">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-orange-500/10" />
              <LazyLoadImage
                src="https://imageio.forbes.com/specials-images/imageserve/65033b85fb3035d47a2250b8/0x0.jpg?format=jpg&height=600&width=1200&fit=bounds"
                alt="Illustration of training data"
                className="object-cover w-full h-full rounded-lg"
                effect="opacity"
              />
            </div>
            <div className="mt-4 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 to-orange-500/5 rounded-xl" />
              <audio controls className="w-full rounded-xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm relative z-10">
                <source src="https://storage.googleapis.com/ashes_project_website_artifacts/audio/TrainingInput.wav" type="audio/wav" />
                Your browser does not support the audio element.
              </audio>
            </div>
          </div>
        </HighlightedTextBlock>
      ),
    },
    {
      title: "TTS Model Comparisons",
      id: slugify("TTS Model Comparisons"),
      content: (
        <HighlightedTextBlock color="purple">
          <div className="space-y-6">
            <p className="text-neutral-700 dark:text-neutral-300 text-base font-normal leading-relaxed">
              So, I knew I needed a TTS model, but finding the <span className="font-medium text-violet-600 dark:text-violet-400">*right*</span> one? That was a mission! Turns out, the world of Text-to-Speech is kinda secretive and focused on making money, so good open-source options are surprisingly hard to find. But, after digging around, I narrowed it down to two of the best open-source models out there right now. Let's see how they stack up!
            </p>
            <div className="relative overflow-hidden rounded-xl aspect-video bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/50 dark:to-purple-950/50 p-1">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-500/10 to-purple-500/10" />
              <LazyLoadImage
                src="https://img.freepik.com/free-photo/colorful-duo-tone-background_23-2148569177.jpg"
                alt="Comparison Image"
                className="object-cover w-full h-full rounded-lg"
                effect="opacity"
              />
            </div>
          </div>
        </HighlightedTextBlock>
      ),
    },
    {
      title: "TTS Model Comparisons: Coqui TTS",
      id: slugify("TTS Model Comparisons: Coqui TTS"),
      content: (
        // Changed: HighlightedTextBlock now wraps the entire item's content div
        <HighlightedTextBlock color="cyan">
          <div className="space-y-6">
            <p className="text-neutral-700 dark:text-neutral-300 text-base font-normal leading-relaxed">
              First up in our TTS showdown is <span className="font-medium text-cyan-600 dark:text-cyan-400">Coqui TTS</span>. This library is a big deal in the TTS world, packed with different models like Tacotron, VITS, and Glow-TTS. It's all about being versatile, handling tons of languages, and even doing things like voice cloning and switching between speakers. Plus, Coqui gives you the tools to train and tweak models yourself, and there's a good community around it if you get stuck. It's a solid, all-around choice for both playing around and real projects.
            </p>
            <div className="relative overflow-hidden rounded-xl aspect-video bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-950/50 dark:to-blue-950/50 p-1">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-blue-500/10" />
              <LazyLoadImage
                src="https://opengraph.githubassets.com/31078a40cfb564f4b0e3a1b7521240647a0c7d4272213a5f3c176b10cb3cf715/coqui-ai/TTS"
                alt="Coqui TTS GitHub"
                className="object-cover w-full h-full rounded-lg"
                effect="opacity"
              />
            </div>
            <div className="mt-4 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-xl" />
              <audio controls className="w-full rounded-xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm relative z-10">
                <source src="https://storage.googleapis.com/ashes_project_website_artifacts/audio/XTTSOutput.wav" type="audio/wav" />
                Your browser does not support the audio element.
              </audio>
            </div>
          </div>
        </HighlightedTextBlock>
      ),
    },
    {
      title: "TTS Model Comparisons: E2/F5 TTS",
      id: slugify("TTS Model Comparisons: E2/F5 TTS"),
      content: (
        // Changed: HighlightedTextBlock now wraps the entire item's content div
        <HighlightedTextBlock color="indigo">
          <div className="space-y-6">
            <p className="text-neutral-700 dark:text-neutral-300 text-base font-normal leading-relaxed">
              But hold on, there's another contender: <span className="font-medium text-indigo-600 dark:text-indigo-400">E2/F5 TTS</span>. Like Coqui, it's also a TTS library with a bunch of models, but it seems to really shine when it comes to voice cloning, especially with its "one-shot" cloning. This means it can do a pretty amazing job of mimicking a voice from just a single, short sample. Let's see how it sounds!
            </p>
            <div className="relative overflow-hidden rounded-xl aspect-video bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/50 dark:to-violet-950/50 p-1">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/10 to-violet-500/10" />
              <LazyLoadImage
                src="https://cdn-thumbnails.huggingface.co/social-thumbnails/spaces/ThreadAbort/E2-F5-TTS.png"
                alt="E2/F5 TTS Hugging Face"
                className="object-cover w-full h-full rounded-lg"
                effect="opacity"
              />
            </div>
            <div className="mt-4 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/5 to-violet-500/5 rounded-xl" />
              <audio controls className="w-full rounded-xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm relative z-10">
                <source src="https://storage.googleapis.com/ashes_project_website_artifacts/audio/E2%3AFEOutput.wav" type="audio/wav" />
                Your browser does not support the audio element.
              </audio>
            </div>
          </div>
        </HighlightedTextBlock>
      ),
    },
    {
      title: "TTS Model Comparisons: Resemble.ai",
      id: slugify("TTS Model Comparisons: Resemble.ai"),
      content: (
        // Changed: HighlightedTextBlock now wraps the entire item's content div
        <HighlightedTextBlock color="black">
          <div className="space-y-6">
            <p className="text-neutral-700 dark:text-neutral-300 text-base font-normal leading-relaxed">
              But hold on, there's another contender: <span className="font-medium text-rose-600 dark:text-rose-400">Resemble.ai</span>. Like Coqui, it's also a TTS library with a bunch of models, but it seems to really shine when it comes to voice cloning, especially with its "one-shot" cloning. This means it can do a pretty amazing job of mimicking a voice from just a single, short sample. Let's see how it sounds!
            </p>
            <div className="relative overflow-hidden rounded-xl aspect-video bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-950/50 dark:to-pink-950/50 p-1">
              <div className="absolute inset-0 bg-gradient-to-r from-rose-500/10 to-pink-500/10" />
              <LazyLoadImage
                src="https://gdm-catalog-fmapi-prod.imgix.net/ProductLogo/3e093209-9de8-4202-8fe1-822572fbca8a.png?w=128&h=128&fit=max&dpr=3&auto=format&q=50"
                alt="Resemble.ai Hugging Face"
                className="object-cover w-full h-full rounded-lg"
                effect="opacity"
              />
            </div>
            <div className="mt-4 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-rose-500/5 to-pink-500/5 rounded-xl" />
              <audio controls className="w-full rounded-xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm relative z-10">
                <source src="https://storage.googleapis.com/ashes_project_website_artifacts/audio/ResembleAI.wav" type="audio/wav" />
                Your browser does not support the audio element.
              </audio>
            </div>
          </div>
        </HighlightedTextBlock>
      ),
    },
    {
      title: "E2/F5 TTS: The Winner for Realistic Voice Cloning",
      id: slugify("E2/F5 TTS: The Winner for Realistic Voice Cloning"),
      content: (
        // Changed: HighlightedTextBlock now wraps the entire item's content div
        <HighlightedTextBlock color="yellow">
          <div className="space-y-6">
            <p className="text-neutral-700 dark:text-neutral-300 text-base font-normal leading-relaxed">
              Okay, after testing them out, it's pretty clear: <span className="font-medium text-emerald-600 dark:text-emerald-400">E2/F5 TTS</span> is the winner for voice cloning that sounds seriously real, especially with that one-shot trick. It just nails the little details and nuances of a voice from just a tiny sample. Plus, it seems to be better at avoiding weird accent issues that can pop up with other TTS systems. Basically, it gives a cleaner, more authentic voice clone that really sounds like the person you're aiming for.
            </p>
            <div className="relative overflow-hidden rounded-xl aspect-video bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/50 dark:to-teal-950/50 p-1">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-teal-500/10" />
              <LazyLoadImage
                src="https://static.vecteezy.com/system/resources/previews/011/351/201/non_2x/elegant-golden-scene-diagonal-glowing-with-lighting-effect-sparkle-on-black-background-template-premium-award-design-vector.jpg"
                alt="E2/F5 TTS Winner Image"
                className="object-cover w-full h-full rounded-lg"
                effect="opacity"
              />
            </div>
          </div>
        </HighlightedTextBlock>
      ),
    },
    {
      title: "Digital likeness",
      id: slugify("Digital likeness"),
      content: (
        // Changed: HighlightedTextBlock now wraps the entire item's content div
        <HighlightedTextBlock color="purple">
          <div className="space-y-6">
            <p className="text-neutral-700 dark:text-neutral-300 text-base font-normal leading-relaxed">
              So, we've got a voice! With the voice cloning working nicely, the next piece of the puzzle is the visual side. I'm starting to look into cloning my <span className="font-medium text-purple-600 dark:text-purple-400">*physical*</span> appearance too, and figuring out how to control this whole digital double. It's all about making it feel like a real, interactive digital version of me!
            </p>
            <div className="relative overflow-hidden rounded-xl aspect-video bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/50 dark:to-indigo-950/50 p-1">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-indigo-500/10" />
              <LazyLoadImage
                src="https://artofvfx.com/wp-content/uploads/2020/07/Westworld_S3_ILP_ITW_01B.jpg"
                alt="Digital likeness illustration"
                className="object-cover w-full h-full rounded-lg"
                effect="opacity"
              />
            </div>
          </div>
        </HighlightedTextBlock>
      ),
    },
    {
      title: "Digital likeness - Starting Point",
      id: slugify("Digital likeness - Starting Point"),
      content: (
        // Changed: HighlightedTextBlock now wraps the entire item's content div
        <HighlightedTextBlock color="blue">
          <div className="space-y-6">
            <p className="text-neutral-700 dark:text-neutral-300 text-base font-normal leading-relaxed">
              In doing my research there are few free or open source options to create a digital twin. Having said that, there is one interesting thing I found, it would seem the <span className="font-medium text-blue-600 dark:text-blue-400">Games industry</span> is very interested in creating unique characters for their games, and thus they are actively working on creating technologies to clone peoples faces and movements to create more realistic game environments. So this has given us a starting point, can we use some of the tools in the gaming industry to create a fully rigged facial mesh that I can use for syncing to my voice files. First step is creating a high fidelity facial scan of my head and shoulders. After that our job is to use that 3D scan and map it to a facial mesh.
            </p>
            <div className="relative overflow-hidden rounded-xl aspect-video bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/50 dark:to-cyan-950/50 p-1">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-500/10" />
              <LazyLoadImage
                src="https://di4d.com/wp-content/uploads/2021/02/NEWS_SNAPPERS.jpg"
                alt="3D Scan illustration"
                className="object-cover w-full h-full rounded-lg"
                effect="opacity"
              />
            </div>
          </div>
        </HighlightedTextBlock>
      ),
    },
    {
      title: "Digital likeness - 3D Head Scan",
      id: slugify("Digital likeness - 3D Head Scan"),
      content: (
        // Changed: HighlightedTextBlock now wraps the entire item's content div
        <HighlightedTextBlock color="teal">
          <div className="space-y-6">
            <p className="text-neutral-700 dark:text-neutral-300 text-base font-normal leading-relaxed">
              <span className="font-medium text-teal-600 dark:text-teal-400">Reality Scan</span>
            </p>
            <div className="relative overflow-hidden rounded-xl aspect-video bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-950/50 dark:to-emerald-950/50 p-1">
              <div className="absolute inset-0 bg-gradient-to-r from-teal-500/10 to-emerald-500/10" />
              <LazyLoadImage
                src="https://cdn2.unrealengine.com/1920x1080-1920x1080-a3545a9d33a4.png"
                alt="Reality Scan illustration"
                className="object-cover w-full h-full rounded-lg"
                effect="opacity"
              />
            </div>
          </div>
        </HighlightedTextBlock>
      ),
    },
  ], []); // Empty dependency array means this data is memoized once

  // Scroll handler for the single item
  const handleSectionChange = useCallback(() => {
    setActiveSectionId(prometheusNavItem.id); // Set as active
    if (mainContentRef.current) {
      // Scroll the main content area to the top
      mainContentRef.current.scrollTop = 0;
    }
  }, []); // No dependencies needed as setter and ref object are stable

  // Styling functions (reused from DS)
  const getButtonStyles = (buttonId) => {
    const isActive = activeSectionId === buttonId;
    // Base classes applied to all buttons
    let baseClasses =
      "flex items-center p-3 rounded-lg transition-colors duration-150 ease-in-out cursor-pointer w-full text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-800";
    
    if (isActive) {
      // Active button specific styles
      return `${baseClasses} bg-gray-100 dark:bg-gray-700 border-2 border-gray-500 dark:border-gray-400 focus:ring-gray-500`;
    }
    // Inactive button specific styles
    return `${baseClasses} bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 focus:ring-gray-500`; 
  };

  const getTextColorAndWeightClass = (buttonId) => {
    const isActive = activeSectionId === buttonId;
    // Text color and font weight classes based on active state
    const textColorClass = isActive ? "text-gray-700 dark:text-gray-200" : "text-gray-700 dark:text-gray-300";
    const fontWeightClass = isActive ? "font-semibold" : "font-medium";
    return `${textColorClass} ${fontWeightClass}`;
  };

  // Configuration for sticky sidebar positioning and max height (reused from DS)
  const fixedSidebarTopOffset = "md:top-16"; // Corresponds to 4rem
  const sidebarMaxHeight = "md:max-h-[calc(100vh-4rem-2rem)]"; // Viewport height - top offset - bottom spacing

  // Render timeline items without redundant wrapping
  const renderTimelineItems = () => {
    return timelineData.map((item, index) => (
      <TimelineItem
        key={item.id}
        content={item.content} // Pass the already structured content directly
        isEven={index % 2 === 0}
      />
    ));
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen flex flex-col">
      {/* Header with WavyBackground */}
      <header className="relative flex flex-col items-center justify-center min-h-[50vh] overflow-hidden">
        <WavyBackground className="w-full h-full flex flex-col items-center justify-center text-center py-20 px-4 md:px-8">
          <h1 className="text-2xl md:text-4xl lg:text-6xl text-black font-bold inter-var leading-tight tracking-tight max-w-4xl bg-clip-text text-transparent bg-gradient-to-r from-neutral-900 to-neutral-600 dark:from-neutral-100 dark:to-neutral-400 animate-gradient">
            Prometheus stole fire and got chained to a rock for eternity. We'll
            take AI—and hope the gods don't notice
          </h1>
        </WavyBackground>
        {/* Subtle bottom border for the header */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent" />
      </header>

      {/* Main content area */}
      <div className="flex-1 flex flex-col md:flex-row max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 md:py-8 gap-8 md:gap-10">
        {/* Sticky Navigation Sidebar */}
        <nav
          className={`md:w-72 lg:w-1/4 shrink-0 md:sticky ${fixedSidebarTopOffset} ${sidebarMaxHeight} md:overflow-y-auto`}
          aria-labelledby="prometheus-navigation-title"
        >
          <div className="space-y-3">
            <h2 id="prometheus-navigation-title" className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 px-3 hidden md:block">
              Project Sections
            </h2>
            {/* Render only the single Prometheus Overview button */}
            <button
              key={prometheusNavItem.id}
              type="button"
              className={`${getButtonStyles(prometheusNavItem.id)}`}
              onClick={handleSectionChange}
              aria-pressed={activeSectionId === prometheusNavItem.id}
            >
              <NotebookIcon
                className="mr-2.5 flex-shrink-0 w-5 h-5 transition-colors duration-200"
                color={activeSectionId === prometheusNavItem.id ? ACTIVE_ICON_COLOR : INACTIVE_ICON_COLOR}
              />
              <span className={`text-sm ${getTextColorAndWeightClass(prometheusNavItem.id)} transition-all duration-200`}>
                {prometheusNavItem.label}
              </span>
            </button>
          </div>
        </nav>

        {/* Main Scrollable Content */}
        <main
          ref={mainContentRef}
          className="flex-1 min-w-0 md:overflow-y-auto relative"
        >
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 pointer-events-none" />
          
          {/* Content container */}
          <div className="relative">
            <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 p-4 sm:p-6 lg:p-8">
              <Timeline data={timelineData} className="-ml-8">
                {renderTimelineItems()}
              </Timeline>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}